import { getEnv } from '@shared/utils/env';

export const COLUMN_SEARCH_TYPES = {
  EMAIL: 'email',
  NAME: 'name',
  TEXT: 'text',
  NUMBER: 'number',
  DEFAULT: 'default',
} as const;

export type ColumnSearchType = (typeof COLUMN_SEARCH_TYPES)[keyof typeof COLUMN_SEARCH_TYPES];

export const getInputPattern = (type: string): RegExp => {
  switch (type) {
    case COLUMN_SEARCH_TYPES.EMAIL:
      return /^[a-zA-Z0-9._%+\-@]*$/;

    case COLUMN_SEARCH_TYPES.NAME:
      return /^[A-Za-záéíóúÁÉÍÓÚñÑüÜ\s]*$/;

    case COLUMN_SEARCH_TYPES.TEXT:
      return /^[\p{L}\p{M}0-9._\s#-]*$/u;

    case COLUMN_SEARCH_TYPES.NUMBER:
      return /^\d*$/;

    default:
      return /^[^'";<>/=]*$/;
  }
};

export const handleInputValidation = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  type: string
): void => {
  const pattern = getInputPattern(type);
  const value = e.target.value;

  const validChars = value
    .split('')
    .filter((char) => pattern.test(char))
    .join('');

  e.target.value = validChars;
};

const DEFAULT_IMAGE_MAX_SIZE_MB = 1;
export const IMAGE_SOURCE_MAX_SIZE_MB = 30;

export const getImageMaxSizeMB = (): number => {
  try {
    const envMaxSize = getEnv().VITE_IMAGE_SIZE_MAX_MB;
    return Number(envMaxSize) || DEFAULT_IMAGE_MAX_SIZE_MB;
  } catch (e) {
    console.warn('Could not read VITE_IMAGE_SIZE_MAX_MB from environment, using default 1MB', e);
    return DEFAULT_IMAGE_MAX_SIZE_MB;
  }
};

export const validImageSize = (value: File | FileList | null | undefined, maxSizeMB?: number): boolean => {
  const file = value && 'length' in value ? (value[0] ?? null) : value;
  if (!file) {
    return true;
  }

  const maxSize = maxSizeMB ?? getImageMaxSizeMB();
  const fileSizeMB = file.size / 1024 / 1024;
  return fileSizeMB <= maxSize;
};

export const isValidChar = (char: string, type: string): boolean => {
  const pattern = getInputPattern(type);
  return pattern.test(char);
};

export const sanitizeByType = (value: string, type: string): string => {
  const pattern = getInputPattern(type);
  return value
    .split('')
    .filter((char) => pattern.test(char))
    .join('');
};

export const sanitizeEmailInput = (value: string): string =>
  value
    .replace(/\s/g, '')
    .replace(/[^a-zA-Z0-9._+@-]/g, '')
    .replace(/^\.+/, '')
    .replace(/\.{2,}/g, '.');

export const sanitizeCustomFundsText = (value: string, maxLength: number): string =>
  value
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .replace(/\b(OR|AND|UNION|SELECT|INSERT|DROP|DELETE)\b/gi, '')
    .slice(0, maxLength);

export const inputValidators = {
  COLUMN_SEARCH_TYPES,
  getInputPattern,
  handleInputValidation,
  getImageMaxSizeMB,
  validImageSize,
  isValidChar,
  sanitizeByType,
  sanitizeEmailInput,
  sanitizeCustomFundsText,
};

export default inputValidators;
